import { Decimal } from 'decimal.js';
import { utils } from 'ethers';
import { capitalize } from 'lodash';
import React, { useState, useContext, useEffect, ReactNode } from 'react';
import { observer } from 'mobx-react';
import { useAccount, useBlockNumber } from 'wagmi';
import { ContractContext } from '../../../context-providers/ContractsProvider';
import { ServicesContext } from '../../../context-providers/ServicesProvider';
import { tokenDecimals } from '../../../helpers/config/contracts.config';
import { formatCurrencyAmount } from '../../../helpers/formatters';
import { calculateTakeProfitParamsDecimal } from '../../../helpers/math/calculate-take-profit-params';
import { calculatePositionSize } from '../../../helpers/math/position-math.helpers';
import { balanceStore } from '../../../stores/BalanceStore';
import { chartStore } from '../../../stores/ChartStore';
import LabelValue from '../../LabelValue/LabelValue';
import NereusButton from '../../NereusButton/NereusButton';
import StatusIcon from '../../StatusIcon/StatusIcon';
import ModalWrapper from '../ModalWrapper/ModalWrapper';
import './OpenPositionDialog.scss';
import {useSwitchChainToBase} from "../../../hooks/useSwitchChainToBase";

const buttonTexts = {
    longAsset: 'Long {asset}',
    shortAsset: 'Short {asset}',
    longingAset: 'Longing {asset}',
    shortingAsset: 'Shorting {asset}'
};

interface OpenPositionDialogProps {
    symbol: string;
    amount: number;
    leverage: number;
    takeProfit?: number | null;
    stopLoss?: number | null;
    onOpenPositionStarted?: () => void;
}

const OpenPositionDialog: React.FC<OpenPositionDialogProps> = observer(({
                                                                            symbol,
                                                                            amount,
                                                                            leverage,
                                                                            stopLoss,
                                                                            takeProfit,
                                                                            onOpenPositionStarted
                                                                        }: OpenPositionDialogProps) => {
    const { data: blockNumber } = useBlockNumber({ watch: true });
    const { address: userAddress } = useAccount();
    const { usdcContract } = useContext(ContractContext);
    const { positionsManager } = useContext(ServicesContext);
    const switchChainToBase = useSwitchChainToBase();

    const [buttonText, setButtonText] = useState<string>('');
    const [isPending, setIsPending] = useState<boolean>(false);
    const [positionSize, setPositionSize] = useState<number>(0);
    const [longShortText, setLongShortText] = useState<string>('');
    const [baseAsset, setBaseAssset] = useState<string>('');
    const [collateralTextValue, setCollateralTextValue] = useState<string>('');
    const [longTextValue, setLongTextValue] = useState<string>('');
    const [takeProfitPriceNode, setTakeProfitPriceNode] = useState<ReactNode | null>(null);
    const [stopLossPriceNode, setStopLossPriceNode] = useState<ReactNode | null>(null);
    const [isLong, setIsLong] = useState<boolean>(true);
    const [isRetry, setIsRetry] = useState(false);

    useEffect(() => {
        let buttonText: string | undefined;
        if (isLong) {
            buttonText = isPending ? buttonTexts.longingAset : buttonTexts.longAsset;
        } else {
            buttonText = isPending ? buttonTexts.shortingAsset : buttonTexts.shortAsset;
        }

        buttonText = buttonText.replace('{asset}', baseAsset);

        if (isRetry && !isPending) {
            buttonText = `Retry ${buttonText}`;
        }

        setButtonText(buttonText);

    }, [isLong, baseAsset, isRetry, isPending]);


    useEffect(() => {
        const isLong = symbol.split(':')[2] === 'long';
        setIsLong(isLong);
    }, [symbol]);

    useEffect(() => {
        if (takeProfit) {
            const amountWithLeverage = new Decimal(amount).mul(leverage);
            const takeProfitParams = calculateTakeProfitParamsDecimal(
                chartStore.latestPrice,
                amountWithLeverage, // ???
                true,
                isLong,
                takeProfit
            );

            setTakeProfitPriceNode(
                <span
                    className="nereus-color-main">{formatCurrencyAmount(takeProfit, 'USDC')} ({Number(takeProfitParams?.closeGainPercent?.toFixed(2))}%)</span>
            );
        } else {
            setTakeProfitPriceNode(null);
        }
    }, [takeProfit, chartStore.latestPrice, amount, leverage, isLong]);

    useEffect(() => {
        const size = calculatePositionSize(chartStore.latestPrice, leverage || 1, amount, isLong);
        setPositionSize(size || 0);
    }, [chartStore.latestPrice, leverage, amount, isLong]);

    useEffect(() => {
        if (stopLoss) {
            const amountWithLeverage = new Decimal(amount).mul(leverage);
            const stopLossParams = calculateTakeProfitParamsDecimal(
                chartStore.latestPrice,
                amountWithLeverage,
                false,
                isLong,
                stopLoss
            );

            setStopLossPriceNode(
                <span
                    className="nereus-color-status-down">{formatCurrencyAmount(stopLoss, 'USDC')} (-{Number(stopLossParams?.closeGainPercent?.toFixed(2))}%)</span>
            );
        } else {
            setStopLossPriceNode(null);
        }
    }, [stopLoss, chartStore.latestPrice, amount, leverage, isLong]);


    useEffect(() => {
        setCollateralTextValue(formatCurrencyAmount(amount, 'USDC'));
    }, [amount]);

    useEffect(() => {
        setLongTextValue(formatCurrencyAmount(positionSize, baseAsset));
    }, [positionSize, baseAsset, amount, leverage]);

    useEffect(() => {
        const [baseAsset, /* quoteAsset - do not remove */, longOrShort] = symbol.split(':');
        const baseAsetUppercase = baseAsset.toUpperCase();
        const longShortText = capitalize(longOrShort);

        setBaseAssset(baseAsetUppercase);
        setLongShortText(longShortText);
    }, [symbol]);

    useEffect(() => {
        if (userAddress && blockNumber !== undefined) {
            balanceStore.fetchTokenBalance(usdcContract, userAddress, Number(blockNumber));
        }
    }, [blockNumber, usdcContract, userAddress]);

    const handleOpenPosition = async () => {
        setIsPending(true);

        const amountBigNumber = utils.parseUnits(amount + '', tokenDecimals.usdc);

        if (onOpenPositionStarted) {
            onOpenPositionStarted();
        }

        try {
            await switchChainToBase()
            await positionsManager.signAndOpenPosition(
                amountBigNumber,
                symbol,
                leverage,
                positionSize,
                chartStore.latestPrice,
                takeProfit || null,
                stopLoss || null,
            );
        } catch (err) {
            setIsRetry(true);
        }

        setIsPending(false);
    };

    return (
        <ModalWrapper className="OpenPositionDialog"
                      headerChildren={<h2 className="nereus-body-m">{`Confirm ${baseAsset} ${longShortText}`}</h2>}
                      bodyChildren={<div className="OpenPositionDialog__body">
                          <div>
                              <LabelValue label="Amount" value={collateralTextValue}/>
                              <LabelValue label={longShortText} value={longTextValue}/>
                              <LabelValue label="Leverage" value={`${leverage}x`}/>
                              <hr className="nereus-bg-neutral-dark-blue mt-4 mb-4"/>

                              <LabelValue label="Open Price" value={formatCurrencyAmount(chartStore.latestPrice, 'USDC')}/>
                              {takeProfit ? <LabelValue label="TP Price" value={takeProfitPriceNode}/> : null}
                              {stopLoss ? <LabelValue label="SL Price" value={stopLossPriceNode}/> : null}
                          </div>

                          <div className="flex pb-4 mt-4">
                              <NereusButton disabled={isPending} variant="contained"
                                            onClick={handleOpenPosition}
                                            className="bg-light-green text-dark-blue w-full">
                                  {buttonText} {isPending && <StatusIcon className="ml-1 inline" status="disabled-pending"/>}
                              </NereusButton>
                          </div>
                      </div>}
        />
    );
});

export default OpenPositionDialog;
